import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import company from '@/assets/img/company.jpg';

import * as S from './style';

type HistoryEntry = {
	lines: string[];
	subtitle: string;
};

type HistorySection = {
	entries: HistoryEntry[];
	title: string;
};

type HistoryPeriod = {
	id: string;
	image: string;
	imageType?: 'asset' | 'url';
	sections: HistorySection[];
	title: string;
};

type HistoryTranslation = {
	periods: HistoryPeriod[];
};

const historyImageMap: Record<string, string> = {
	company,
};

const SECTION_NAV_SELECTOR = 'nav[aria-label="Section navigation"]';

const renderLines = (lines: string[]) =>
	lines.map((line, index) => (
		<Fragment key={`${line}-${index}`}>
			{index > 0 && <br />}
			{line}
		</Fragment>
	));

const History = () => {
	const { t } = useTranslation();
	const { periods } = t('introduce.history', { returnObjects: true }) as HistoryTranslation;
	const [date, setDate] = useState(periods[0]?.id ?? '');
	const containerRef = useRef<HTMLElement>(null);
	const stickyBarRef = useRef<HTMLDivElement>(null);
	const isScrollingRef = useRef(false);

	const measureStickyTop = useCallback(() => {
		const header = document.getElementById('header')?.offsetHeight ?? 0;
		const sectionNav = document.querySelector<HTMLElement>(SECTION_NAV_SELECTOR);
		const tabBar = sectionNav?.offsetHeight ?? 0;
		const stickyTop = header + tabBar;

		containerRef.current?.style.setProperty('--history-sticky-top', `${stickyTop}px`);
	}, []);

	useLayoutEffect(() => {
		measureStickyTop();
		window.addEventListener('resize', measureStickyTop);
		return () => window.removeEventListener('resize', measureStickyTop);
	}, [measureStickyTop]);

	const getScrollOffset = useCallback(() => {
		const header = document.getElementById('header')?.offsetHeight ?? 0;
		const sectionNav = document.querySelector<HTMLElement>(SECTION_NAV_SELECTOR);
		const tabBar = sectionNav?.offsetHeight ?? 0;
		const periodBar = stickyBarRef.current?.offsetHeight ?? 52;
		return header + tabBar + periodBar + 8;
	}, []);

	const moveToContent = useCallback(
		(tab: string) => {
			const target = document.getElementById(tab);
			if (!target) return;

			isScrollingRef.current = true;
			const top = target.getBoundingClientRect().top + window.scrollY - getScrollOffset();
			window.scrollTo({ top, behavior: 'smooth' });

			window.setTimeout(() => {
				isScrollingRef.current = false;
			}, 600);
		},
		[getScrollOffset],
	);

	const onClickTabBar = (tab: string) => {
		setDate(tab);
		moveToContent(tab);
	};

	useEffect(() => {
		if (!periods.length) return;

		let observer: IntersectionObserver | null = null;

		const setupObserver = () => {
			observer?.disconnect();

			const periodBarHeight = stickyBarRef.current?.offsetHeight ?? 52;
			const header = document.getElementById('header')?.offsetHeight ?? 0;
			const sectionNav = document.querySelector<HTMLElement>(SECTION_NAV_SELECTOR);
			const tabBar = sectionNav?.offsetHeight ?? 0;
			const topOffset = header + tabBar + periodBarHeight;

			observer = new IntersectionObserver(
				(entries) => {
					if (isScrollingRef.current) return;

					const visible = entries
						.filter((entry) => entry.isIntersecting)
						.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

					const current = visible[0];
					if (current?.target.id) {
						setDate(current.target.id);
					}
				},
				{
					rootMargin: `-${topOffset}px 0px -60% 0px`,
					threshold: 0,
				},
			);

			periods.forEach((period) => {
				const el = document.getElementById(period.id);
				if (el) observer?.observe(el);
			});
		};

		setupObserver();
		window.addEventListener('resize', setupObserver);

		return () => {
			window.removeEventListener('resize', setupObserver);
			observer?.disconnect();
		};
	}, [periods]);

	const getImageSource = (period: HistoryPeriod) => {
		if (period.imageType === 'asset') {
			return historyImageMap[period.image] ?? company;
		}

		return period.image;
	};

	return (
		<S.HistoryContainer ref={containerRef}>
			<S.HistoryStickyBar ref={stickyBarRef} id="history-period-bar">
				<S.HistoryDateList role="tablist" aria-label={t('학회연혁')}>
					{periods.map((period) => (
						<S.HistoryDate
							key={period.id}
							type="button"
							role="tab"
							aria-selected={period.id === date}
							className={period.id === date ? 'active' : ''}
							onClick={() => onClickTabBar(period.id)}
						>
							{period.title}
						</S.HistoryDate>
					))}
				</S.HistoryDateList>
			</S.HistoryStickyBar>

			<S.HistoryContents>
				{periods.map((period) => (
					<S.HistoryContentWrapper key={period.id} id={period.id}>
						<S.HistoryImgBox bgImg={getImageSource(period)} />
						<S.HistoryContentBox>
							<S.HistoryContentMainTitle>{period.title}</S.HistoryContentMainTitle>
							{period.sections.map((section) => (
								<S.HistoryContentItemBox key={`${period.id}-${section.title}`}>
									<S.HistoryContentTitle>{section.title}</S.HistoryContentTitle>
									{section.entries.map((entry) => (
										<S.HistoryContentItem key={`${period.id}-${section.title}-${entry.subtitle}`}>
											<S.HistoryContentSubTitle>{entry.subtitle}</S.HistoryContentSubTitle>
											<S.HistoryContentDescription>{renderLines(entry.lines)}</S.HistoryContentDescription>
										</S.HistoryContentItem>
									))}
								</S.HistoryContentItemBox>
							))}
						</S.HistoryContentBox>
					</S.HistoryContentWrapper>
				))}
			</S.HistoryContents>
		</S.HistoryContainer>
	);
};

export default History;
